import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import FocusLock from 'react-focus-lock'

import { useDynamicMenuControl } from '../../../hooks/useDynamicMenuControl'

export type DatePickerDialogProps = {
  open: boolean
  onClose?: () => void
  renderTarget: (targetProps: { 'aria-haspopup': 'dialog' }) => ReactNode
  renderPicker: () => ReactNode
}

export const DialogFocusContext = createContext({ returnFocus: (): void => undefined })

const RelativeContainer = styled.div`
  position: relative;
`

export const DatePickerDialog = ({ open, onClose, renderTarget, renderPicker }: DatePickerDialogProps) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const datepickerRef = useRef<HTMLDivElement | null>(null)
  const [focusInitiator, setFocusInitiator] = useState<HTMLElement | null>(null)

  const returnFocus = useCallback(() => focusInitiator?.focus({ preventScroll: true }), [focusInitiator])

  const handleClose = useCallback(() => {
    onClose?.()
    returnFocus()
  }, [onClose, returnFocus])

  const { refs, floatingStyles } = useDynamicMenuControl({
    controlRef: targetRef,
    isOpen: open,
    onClose: handleClose,
  })

  // Handle click outside to close dialog
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isInsideTargetElement = targetRef.current?.contains(target)

      const floatingElement = refs.floating?.current
      const isInsideModal =
        floatingElement && floatingElement instanceof HTMLElement && floatingElement.contains(target)

      if (isInsideTargetElement || isInsideModal) {
        return
      }

      handleClose()
    }

    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [open, handleClose, refs.floating])

  // Handle escape key to close dialog
  useEffect(() => {
    if (!open) return

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey, true)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true)
    }
  }, [open, handleClose])

  return (
    <DialogFocusContext.Provider value={{ returnFocus }}>
      <RelativeContainer
        onFocus={event => {
          if (!open) {
            setFocusInitiator(event.target)
          }
        }}
      >
        <FocusLock returnFocus={false} disabled={!open} data-testid="date-picker-focus-lock" shards={[datepickerRef]}>
          <div ref={targetRef}>{renderTarget({ 'aria-haspopup': 'dialog' })}</div>
        </FocusLock>
        {open &&
          createPortal(
            <div
              ref={node => {
                datepickerRef.current = node
                refs.setFloating(node)
              }}
              role="dialog"
              style={floatingStyles}
            >
              {renderPicker()}
            </div>,
            document.body
          )}
      </RelativeContainer>
    </DialogFocusContext.Provider>
  )
}
